import { BillLevel, BillType, MovementType, ToOrder } from '@/const'
import type { Cargo, ManifestDetail } from '@/typings/bill'
import {
  createContactInfo,
  createDefaultCargo,
  createDefaultContainer,
  createBillModel,
} from '@/utils/bill-model'

import { v4 as uuid } from 'uuid'
import Decimal from 'decimal.js'

export function masterCargoTotals(cargos: Cargo[]) {
  const sum = (field: 'packages' | 'grossWeight') =>
    cargos
      .reduce((total, cargo) => {
        const value = Number(cargo[field])
        return Number.isFinite(value) ? total.plus(value) : total
      }, new Decimal(0))
      .toString()
  return { packages: sum('packages'), grossWeight: sum('grossWeight') }
}

/** 仅复用纯数据工厂；主单不经过 HBL 初始化或业务校验。 */
export function createMasterBill(): ManifestDetail {
  return {
    ...createBillModel(),
    billLevel: BillLevel.MASTER,
    masterBillNo: '',
    voyageNo: '',
    imoNo: '',
    vesselName: '',
    portOfDepartureCode: '',
    portOfDeparture: '',
    portOfDepartureEn: '',
    portOfArrivalCode: '',
    portOfArrival: '',
    portOfArrivalEn: '',
    portOfTransshipmentCode: '',
    portOfTransshipment: '',
    portOfTransshipmentEn: '',
    eta: '',
    previousBillType: BillType.EMPTY,
    consolidatedIndicator: false,
    parties: [createContactInfo('SN'), createContactInfo('CO')],
  }
}

export function initializeMasterBill(
  data: Partial<ManifestDetail> & { cargos?: Cargo[] },
): ManifestDetail {
  const defaults = createMasterBill()
  const detail = { ...defaults, ...data }
  for (const key of Object.keys(defaults) as (keyof ManifestDetail)[]) {
    if (detail[key] == null) Object.assign(detail, { [key]: defaults[key] })
  }
  detail.billLevel = BillLevel.MASTER
  if (detail.billType !== ToOrder.NEG && detail.billType !== ToOrder.NON) {
    detail.billType = ToOrder.NON
  }
  detail.houseBillNo = ''
  detail.previousBillType = BillType.EMPTY
  detail.previousHouseBillNo = ''
  detail.previousMpciPartyId = ''
  detail.parties = [
    {
      ...createContactInfo('SN'),
      ...data.parties?.find((p) => p.partyType === 'SN'),
    },
    {
      ...createContactInfo('CO'),
      ...data.parties?.find((p) => p.partyType === 'CO'),
    },
  ]
  detail.containers = data.containers?.length
    ? data.containers.map((container) => {
        const groupId = container.groupId || uuid()
        const cargos = data.cargos
          ? data.cargos.filter((cargo) => cargo.groupId === container.groupId)
          : container.cargos
        return {
          ...createDefaultContainer(),
          ...container,
          groupId,
          cargos: cargos?.length
            ? cargos.map((cargo) => ({
                ...createDefaultCargo(groupId),
                ...cargo,
                groupId,
              }))
            : [createDefaultCargo(groupId)],
        }
      })
    : [createDefaultContainer()]
  delete (detail as ManifestDetail & { cargos?: Cargo[] }).cargos
  if (!detail.consolidatedIndicator) detail.nextMpciPartyId = ''
  if (detail.movementType !== MovementType.TRANSSHIPMENT) {
    detail.portOfTransshipmentCode = ''
    detail.portOfTransshipment = ''
    detail.portOfTransshipmentEn = ''
  }
  return detail
}

export function copyMasterBill(data: ManifestDetail): ManifestDetail {
  const copy = initializeMasterBill(JSON.parse(JSON.stringify(data)))
  copy.id = ''
  copy.masterBillNo = ''
  copy.houseBillOperation = createMasterBill().houseBillOperation
  copy.billStatus = createMasterBill().billStatus
  copy.houseBillOperationId = ''
  copy.userRemark = ''
  delete (copy as ManifestDetail & { draftId?: string }).draftId
  delete (copy as ManifestDetail & { batchNo?: string }).batchNo
  copy.parties.forEach((party) => {
    party.id = ''
  })
  copy.containers.forEach((container) => {
    container.id = ''
    container.groupId = uuid()
    container.cargos.forEach((cargo) => {
      cargo.id = ''
      cargo.groupId = container.groupId
    })
  })
  return copy
}

export function masterBillPayload(detail: ManifestDetail) {
  const model = initializeMasterBill(JSON.parse(JSON.stringify(detail)))
  return {
    ...model,
    cargos: model.containers.flatMap((container) =>
      container.cargos.map((cargo) => ({
        ...cargo,
        containerNo: container.containerNo,
      })),
    ),
    containers: model.containers.map(
      ({ cargos: _cargos, ...container }) => container,
    ),
  }
}
